import { Button, Input } from '@atlas/ui-primitives'
import { useState } from 'react'
import type { PromptModelConfig } from '@/domain/prompt.types'

/** The fields an author may override, in the order they read them. */
const FIELDS = [
	{ key: 'model', label: 'Model', kind: 'text' },
	{ key: 'temperature', label: 'Temperature', kind: 'number', hint: '0–2' },
	{ key: 'maxTokens', label: 'Max tokens', kind: 'number' },
	{ key: 'provider', label: 'Provider', kind: 'text' },
] as const satisfies readonly { key: string; label: string; kind: 'text' | 'number'; hint?: string }[]

/**
 * Per-prompt OVERRIDES of the tenant default config.
 *
 * Every field is optional and an empty one means "inherit", which is the whole point
 * of the defaults: an author changes temperature alone without restating the model.
 * So a blank is stored as absent rather than as an empty string, and the panel keeps
 * showing the inherited value underneath.
 *
 * `negativePrompt` sits apart because it is prose and only image models take one — it
 * needs a wide field, and grouping it with the numeric knobs made both harder to read.
 */
export function PromptModelConfigEditor({
	config,
	inherited,
	isSaving,
	onSave,
	onCancel,
}: {
	config: Partial<PromptModelConfig> | null | undefined
	/** The tenant default, shown as the value a blank field falls back to. */
	inherited: Record<string, unknown>
	isSaving: boolean
	onSave: (next: Partial<PromptModelConfig>) => void
	onCancel: () => void
}) {
	const [draft, setDraft] = useState<Record<string, string>>(() => {
		const current = (config ?? {}) as Record<string, unknown>
		return Object.fromEntries(
			[...FIELDS.map((f) => f.key), 'negativePrompt'].map((key) => [
				key,
				current[key] === undefined || current[key] === null ? '' : String(current[key]),
			]),
		)
	})

	const set = (key: string, value: string) => setDraft((prev) => ({ ...prev, [key]: value }))

	const invalidTemperature = draft.temperature !== '' && !isInRange(draft.temperature, 0, 2)

	return (
		<div className="space-y-3">
			<div className="grid gap-3 sm:grid-cols-2">
				{FIELDS.map((field) => (
					<label key={field.key} className="block">
						<span className="mb-1 block text-ds-2xs text-ds-text-subtle">
							{field.label}
							{'hint' in field ? ` (${field.hint})` : ''}
						</span>
						<Input
							value={draft[field.key] ?? ''}
							aria-label={field.label}
							type={field.kind === 'number' ? 'number' : 'text'}
							// The inherited value as the placeholder, so a blank field visibly
							// means "inherit that" rather than "unset".
							placeholder={inherited[field.key] === undefined ? 'inherited' : String(inherited[field.key])}
							onChange={(e) => set(field.key, e.target.value)}
						/>
					</label>
				))}
			</div>

			<label className="block">
				<span className="mb-1 block text-ds-2xs text-ds-text-subtle">
					Negative prompt — content to exclude, for the models that accept one
				</span>
				<Input
					value={draft.negativePrompt ?? ''}
					aria-label="Negative prompt"
					placeholder="no text, no watermark"
					onChange={(e) => set('negativePrompt', e.target.value)}
				/>
			</label>

			{invalidTemperature ? (
				<p role="alert" className="text-ds-2xs text-ds-danger">
					Temperature must be between 0 and 2.
				</p>
			) : null}

			<div className="flex items-center gap-2">
				<Button size="sm" isDisabled={isSaving || invalidTemperature} onPress={() => onSave(toConfig(draft))}>
					{isSaving ? 'Saving…' : 'Save config'}
				</Button>
				<Button size="sm" variant="muted" isDisabled={isSaving} onPress={onCancel}>
					Cancel
				</Button>
			</div>
		</div>
	)
}

const isInRange = (text: string, min: number, max: number): boolean => {
	const value = Number(text)
	return Number.isFinite(value) && value >= min && value <= max
}

/**
 * The draft as overrides, with blanks OMITTED rather than sent as empty.
 *
 * A key present with an empty value would override the tenant default with nothing,
 * which is the opposite of what clearing a field means here.
 */
function toConfig(draft: Record<string, string>): Partial<PromptModelConfig> {
	const next: Record<string, unknown> = {}

	for (const field of FIELDS) {
		const raw = draft[field.key]?.trim() ?? ''
		if (raw === '') continue
		next[field.key] = field.kind === 'number' ? Number(raw) : raw
	}

	const negative = draft.negativePrompt?.trim() ?? ''
	if (negative !== '') next.negativePrompt = negative

	return next as Partial<PromptModelConfig>
}
